import { z } from 'zod'

/** Mirror of `@nwwp/database` investableAssetsRangeSchema — local so client UI avoids the Dynamo barrel. */
const investableAssetsRangeSchema = z.enum([
  'UNDER_100K',
  'FROM_100_TO_250K',
  'FROM_250_TO_500K',
  'FROM_500K_TO_1M',
  'OVER_1M',
])

const currencySchema = z.coerce.number().min(0).max(9_999_999_999)

const baseLeadMagnetSchema = z
  .object({
    age: z.coerce.number().min(18, 'You must be at least 18 years old').max(120, 'Are you really this old?'),
    household_income: currencySchema,
    first_name: z.string().min(1).max(50),
    last_name: z.string().min(1).max(50),
    email: z.email('Please enter a valid email'),
    phone: z
      .string()
      .min(10, 'Please enter a valid phone number')
      .max(10, 'Please enter a valid phone number')
      .transform((phone) => `+1${phone}`),
    schedule_call_interest: z.boolean().optional(),
    investable_assets_range: investableAssetsRangeSchema,
  })
  .partial({ age: true, household_income: true })
  .extend({
    phone: z.preprocess(
      (val) => (val === '' ? undefined : val),
      z
        .string()
        .min(10, 'Please enter a valid phone number')
        .max(10, 'Please enter a valid phone number')
        .transform((phone) => `+1${phone}`)
        .optional()
    ),
    household_income: z.preprocess((val) => (val === '' ? undefined : val), currencySchema.optional()),
    first_name: z.preprocess((val) => (val === '' ? undefined : val), z.string().min(1).max(50).optional()),
    last_name: z.preprocess((val) => (val === '' ? undefined : val), z.string().min(1).max(50).optional()),
    /** Exact retirement savings in dollars — never rendered as a field; supplied by calculator hosts. */
    current_retirement: z.number().nonnegative().optional(),
  })

/**
 * Builds a schema that requires fields only when they are present in the
 * rendered fields list — prevents silent pass-through when a field is rendered
 * but the user hasn't filled it in.
 */
export function buildLeadMagnetSchema(renderedFields: string[], { phoneRequired = false } = {}) {
  return baseLeadMagnetSchema.superRefine((data, ctx) => {
    if (phoneRequired && renderedFields.includes('phone') && !data.phone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Phone number is required',
        path: ['phone'],
      })
    }
    if (renderedFields.includes('household_income') && data.household_income == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Household income is required',
        path: ['household_income'],
      })
    }
    if (renderedFields.includes('age') && data.age == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Age is required',
        path: ['age'],
      })
    }
    if (renderedFields.includes('first_name') && !data.first_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'First name is required',
        path: ['first_name'],
      })
    }
    if (renderedFields.includes('last_name') && !data.last_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Last name is required',
        path: ['last_name'],
      })
    }
  })
}

export const leadMagnetSchema = baseLeadMagnetSchema

export type LeadMagnetSchema = z.infer<typeof baseLeadMagnetSchema>
